import {
  ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  ConfiguredActionItemDto,
  InspectionEntryFilterDto,
  InspectionEntryItemDto,
  InspectionEntryService,
  InspectionEntryStatisticGroupDto,
  SortDto,
  TenantItemDto,
} from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { currentTenant } from '@/utils/admin-utils';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { userFacingMessage } from '@/utils/user-facing-message';
import { environment } from '../../../environments/environment';
import {
  InspectionEntryStatisticDialogComponent,
} from './inspection-entry-statistic-dialog/inspection-entry-statistic-dialog.component';

const GROUPED_LOAD_FAILED = 'Die gruppierte Auswertung konnte nicht geladen werden.';

const groupedLoadErrorMessage = (error: unknown): string =>
  (error instanceof HttpErrorResponse ? userFacingMessage(error.error) : undefined) ?? GROUPED_LOAD_FAILED;

@Component({
  standalone: false,
  selector: 'wr-inspection-entry-statistic-page',
  templateUrl: './inspection-entry-statistic-page.component.html',
  styleUrls: ['./inspection-entry-statistic-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionEntryStatisticPageComponent {

  private readonly inspectionEntryService = inject(InspectionEntryService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  private readonly http = inject(HttpClient);

  private readonly pageSize = 500;

  currentTenant = currentTenant();
  filterForm = signal(this.createForm());
  selectedBuildingIds = signal<string[]>([]);

  entries = signal<InspectionEntryItemDto[]>([]);
  groups = signal<InspectionEntryStatisticGroupDto[]>([]);
  collapsedGroups = signal<ReadonlySet<string>>(new Set());
  groupedLoadError = signal<string | null>(null);
  // Maßnahmen, die in den gefilterten Prüfpunktzeilen wirklich vorkommen — die
  // tenant-konfigurierte Liste des Selects allein wäre unvollständig (WR-2317)
  availableActions = signal<ConfiguredActionItemDto[]>([]);
  loading = signal<boolean>(false);
  loadingMore = signal<boolean>(false);
  hasMore = signal<boolean>(true);
  exporting = signal<boolean>(false);
  sort = signal<SortDto | undefined>(undefined);

  readonly sortableColumns = [
    { key: 'sectionText', label: 'Adresse' },
    { key: 'inspectionSendingTimestamp', label: 'Datum' },
    { key: 'deadline', label: 'Frist' },
    { key: 'action', label: 'Maßnahme' },
  ] as const;

  readonly groupByOptions: { value: InspectionEntryFilterDto.GroupByEnum; label: string }[] = [
    { value: 'PROPERTY_MANAGER', label: 'Verwalter' },
    { value: 'TEAM', label: 'Team' },
    { value: 'REGION', label: 'Region' },
  ];

  private page = 0;
  private resetVersion = 0;
  private availableActionsKey = '';

  private createForm() {
    const tenant = this.currentTenant();
    let externalTenantInitialValue: TenantItemDto | undefined;
    if (tenant?.type === 'External') externalTenantInitialValue = tenant;

    const form = createForm(({ field }) => ({
      externalTenantId: field({ value: externalTenantInitialValue?.id }),
      internalTenantId: field({ value: undefined as string | undefined }),
      checkpointId:     field({ value: undefined as string | undefined }),
      action:           field({ value: undefined as ConfiguredActionItemDto | undefined }),
      groupBy:          field({ value: undefined as InspectionEntryFilterDto.GroupByEnum | undefined }),
    }));

    form.controls.internalTenantId.valueChanges.subscribe(() => {
      form.controls.action.setValue(undefined, { emitEvent: false });
    });
    form.valueChanges.subscribe(() => this.reset());
    return form;
  }

  toggleSort(key: string): void {
    const current = this.sort();
    this.sort.set({
      sort: key,
      order:
        current?.sort === key && current.order === SortDto.OrderEnum.Asc
          ? SortDto.OrderEnum.Desc
          : SortDto.OrderEnum.Asc,
    });
    this.reset();
  }

  onBuildingsChange(ids: string[]): void {
    this.selectedBuildingIds.set(ids);
    this.reset();
  }

  toggleGroup(key: string): void {
    this.collapsedGroups.update(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  isCollapsed(key: string): boolean {
    return this.collapsedGroups().has(key);
  }

  reset(): void {
    this.resetVersion += 1;
    this.page = 0;
    this.entries.set([]);
    this.groups.set([]);
    this.collapsedGroups.set(new Set());
    this.groupedLoadError.set(null);
    this.hasMore.set(true);
    this.loading.set(false);
    this.loadingMore.set(false);
    void this.loadAvailableActions();
    void this.loadMore();
  }

  // Auswahlliste des Maßnahmen-Filters aus dem echten Datenbestand. Bewusst an dieselbe
  // Vorbedingung wie die Liste gebunden (Tenant + Prüfpunkt): ohne Prüfpunkt müsste der
  // Server über alle Zeilen des Tenants aggregieren.
  private async loadAvailableActions(): Promise<void> {
    const controls = this.filterForm().controls;
    const internalTenantId = controls.internalTenantId.value;
    const checkpointId = controls.checkpointId.value;
    const externalTenantId = controls.externalTenantId.value ?? undefined;
    const buildingIds = this.selectedBuildingIds();

    if (!internalTenantId || !checkpointId) {
      this.availableActionsKey = '';
      this.availableActions.set([]);
      return;
    }

    // die gewählte Maßnahme geht absichtlich nicht in den Key ein — sie schränkt die
    // Auswahlliste nicht ein (der Server ignoriert actionId für diese Abfrage ebenfalls)
    const key = JSON.stringify([internalTenantId, checkpointId, externalTenantId, buildingIds]);
    if (key === this.availableActionsKey) return;
    this.availableActionsKey = key;

    try {
      const actions = await firstValueFrom(this.inspectionEntryService.findStatisticActions({
        externalTenantId,
        internalTenantId,
        checkpointId,
        buildingIds: buildingIds.length > 0 ? buildingIds : undefined,
      }));
      if (key !== this.availableActionsKey) return;
      this.availableActions.set(actions);
    } catch (error) {
      // Key freigeben, damit der nächste Filterwechsel es erneut versucht; das Select
      // fällt derweil auf die tenant-konfigurierten Maßnahmen zurück
      if (key === this.availableActionsKey) this.availableActionsKey = '';
      console.error('Loading statistic actions failed:', error);
    }
  }

  async loadMore(): Promise<void> {
    if (this.loading() || this.loadingMore() || !this.hasMore()) return;

    const controls = this.filterForm().controls;
    const internalTenantId = controls.internalTenantId.value;
    const checkpointId = controls.checkpointId.value;
    if (!internalTenantId || !checkpointId) return;

    const groupBy = controls.groupBy.value;
    if (groupBy) {
      await this.loadGrouped(groupBy);
      return;
    }

    const isFirstPage = this.page === 0;
    (isFirstPage ? this.loading : this.loadingMore).set(true);

    const version = this.resetVersion;

    try {
      const batch = await firstValueFrom(this.inspectionEntryService.findAllForStatistics({
        ...this.currentFilter(),
        page: this.page,
        size: this.pageSize,
      }));

      if (version !== this.resetVersion) return;

      this.page += 1;
      if (batch.length < this.pageSize) this.hasMore.set(false);
      this.entries.update(current => [...current, ...batch]);
    } finally {
      if (version === this.resetVersion) {
        this.loading.set(false);
        this.loadingMore.set(false);
      }
    }
  }

  private async loadGrouped(groupBy: InspectionEntryFilterDto.GroupByEnum): Promise<void> {
    this.loading.set(true);
    const version = this.resetVersion;

    try {
      const groups = await firstValueFrom(this.inspectionEntryService.findAllForStatisticsGrouped({
        ...this.currentFilter(),
        groupBy,
      }));

      if (version !== this.resetVersion) return;

      this.groups.set(groups);
      this.hasMore.set(false);
    } catch (error) {
      if (version === this.resetVersion) this.groupedLoadError.set(groupedLoadErrorMessage(error));
      console.error('Loading grouped statistic failed:', error);
    } finally {
      if (version === this.resetVersion) this.loading.set(false);
    }
  }

  private currentFilter(): InspectionEntryFilterDto {
    const controls = this.filterForm().controls;
    const buildingIds = this.selectedBuildingIds();
    return {
      externalTenantId: controls.externalTenantId.value ?? undefined,
      internalTenantId: controls.internalTenantId.value ?? undefined,
      checkpointId:     controls.checkpointId.value ?? undefined,
      actionId:         controls.action.value?.id,
      buildingIds:      buildingIds.length > 0 ? buildingIds : undefined,
      sort:             this.sort(),
    };
  }

  openDialog(item: InspectionEntryItemDto): void {
    this.dialogService.open(InspectionEntryStatisticDialogComponent, {
      context: { item },
    });
  }

  async exportAllPdf(): Promise<void> {
    if (this.exporting()) {
      return;
    }
    this.exporting.set(true);
    const filter: InspectionEntryFilterDto = {
      ...this.currentFilter(),
      groupBy: this.filterForm().controls.groupBy.value ?? undefined,
    };
    try {
      const blob = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/api/inspectionEntry/exportPdf`, filter, {
          responseType: 'blob',
          headers: { 'Accept': 'application/pdf' },
        }),
      );
      if (!blob || blob.size === 0) {
        this.toastrService.info('Für diesen Filter gibt es keine Prüfpunktzeilen zum Exportieren.', 'Export');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pruefpunktzeilen.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      this.exporting.set(false);
    }
  }
}
